"use client";

import { Box, BoxProps } from "@mui/material";
import api from "@v2/lib/api";
import Image, { ImageProps } from "next/image";
import { useState } from "react";


// Every image address that finished loading in this tab. A page rebuilt by a client
// navigation - the list after the back button - mounts its images again, and the browser
// hands them over from its cache at once; starting those as loaded keeps the placeholder
// and the fade-in from playing a second time over a photo that is already there. Empty on
// the server and on the first render after it, so hydration sees the same markup.
const loadedSrcs = new Set<string>()

const MediaV2 = (props: MediaV2Props) => {
    const src = generateMediaSrc(props.media_id, { ...props.options, height: props.height, width: props.width })
    const [loaded, setLoaded] = useState(() => loadedSrcs.has(src))
    return (
        <Box {...props.boxProps} className={loaded ? `loaded ${props.boxProps?.className}` : props.boxProps?.className} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',

            ...props.boxProps?.sx,


            '& img': {
                width: '100%',
                height: 'auto'
            },

            ...(props.withLoading ? {
                '& img.__media_image': {
                    opacity: 0,
                    transform: 'scale(0.9)',
                    transition: '300ms',
                    borderRadius: '50%'
                },
                '&.loaded': {
                    ...props.boxProps?.sx['&.loaded'],
                    '& .loading-snipper': {
                        display: 'none'
                    },
                    '& img.__media_image': {
                        opacity: 1,
                        transform: 'scale(1)',
                        borderRadius: 0
                    }
                },
            } : {}),

            ...(props.blurSrc ? {
                '& img.__media_image': {
                    // opacity: 0,
                    // transform: 'scale(0.9)',
                    transition: '300ms',
                    // borderRadius: '50%'
                },
                '& .loading-snipper': {
                    transition: '450ms',
                    filter: 'blur(6px)'
                },
                '&.loaded': {
                    ...props.boxProps?.sx['&.loaded'],
                    '& .loading-snipper': {
                        opacity: 0,
                    },
                    '& img.__media_image': {
                        opacity: 1,
                    }
                },
            } : {})




            // ...(props.blurSrc ? {
            //     filter: 'blur(6px)',
            //     transition: '350ms',
            //     '&.loaded': {
            //         filter: 'blur(0)',
            //     }
            // } : {})
        }}>
            {(props.withLoading || props.blurSrc) && (
                <Box className="loading-snipper" sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& > img': {
                        width: '100%',
                        // height: 'auto'
                    }
                }}>
                    {props.blurSrc ? (
                        <Image src={props.blurSrc} alt=""
                            priority unoptimized
                            fetchPriority="high"
                            // width={400} height={320}
                            fill
                            style={{
                                objectFit: 'contain',
                                // animation: 'media-placeholder-appear-loop 3.5s infinite'
                            }} />
                    ) : (
                        <Image src={"/assets/new_placeholder.webp"} alt={props.alt || "placeholder"}
                            fill
                            priority unoptimized style={{
                                objectFit: 'contain',
                                animation: 'media-placeholder-appear-loop 3.5s infinite',
                            }} />
                    )}
                </Box>
            )}



            <Image
                className="__media_image"
                height={!props.imageProps?.fill ? props.height : undefined}
                width={!props.imageProps?.fill ? props.width : undefined}
                src={src}
                alt={props.alt ?? ""}
                title={props.title ?? undefined}
                onError={!props.disablePlaceholder ? (e) => (e.currentTarget.src = "/assets/new_placeholder.webp") : undefined}
                onLoad={() => {
                    loadedSrcs.add(src)
                    setLoaded(true) // e.currentTarget.parentElement.classList.add('loaded')
                }}


                {...props.imageProps}

                // placeholder={'blur'}
                // blurDataURL={props.blurSrc}
                unoptimized


            />
        </Box>
    )
};

export const generateMediaSrc = (id: string, options?: MediaV2Props['options'] & {
    height?: number,
    width?: number
}) => {
    const params = new URLSearchParams();
    const action = options?.action ?? "cropThumbnail";
    params.set("action", action.toString());

    if (options?.height) params.set("height", options.height.toString());
    if (options?.width) params.set("width", options.width.toString());
    if (options?.opacity) params.set("opacity", options?.opacity.toString());
    if (options?.quality) params.set("quality", options?.quality.toString());
    if (options?.background) params.set("background", options?.background.toString());
    if (options?.fill) params.set("fill", '1');
    if (options?.bestFill) params.set("bestFill", '1');
    if (options?.requestPoster) params.set("request_poster", '1');
    if (options?.aspectRatio) params.set("aspectRatio", options.aspectRatio);
    if (options?.aspectRatio && options?.position) params.set("position", options.position);

    const stringifyParams = params.toString();
    const src = api.media + "/" + id + (stringifyParams ? "?" + stringifyParams : "");

    return src;
}

// A video poster is drawn twice: once by the stream wrapper while the player loads,
// and once by the player itself as its start cover. Both must build the exact same
// URL -- the media server keys its cache on the whole query, so a different `action`
// or a different width is a second full download of a file the viewer already has.
export const VIDEO_POSTER_SIZE = { width: 900, height: 506 };

export const generateVideoPosterSrc = (posterMediaId: string) =>
    generateMediaSrc(posterMediaId, { width: VIDEO_POSTER_SIZE.width, quality: 80 });

export interface MediaV2Props {
    /**
     * server side id
     */
    media_id: null | string;

    withLoading?: boolean;

    boxProps?: BoxProps,
    imageProps?: Omit<ImageProps, 'src' | 'alt'>,
    height?: number,
    width?: number,
    alt: string,
    title?: string,

    disablePlaceholder?: boolean,

    blurSrc?: string,

    options?: {
        /**
         * @default `cropThumbnail`
         */
        action?: "thumbnail" | "cropThumbnail";

        fill?: boolean;
        bestFill?: boolean;

        /**
         * @value color like `rgb(0,0,0)`
         */
        background?: string;

        /**
         * @value `0` to `1`
         */
        opacity?: number;

        /**
         * @value `1` to `100`
         * @default 92
         */
        quality?: number;

        isGlass?: boolean,

        /**
         * Asks the server to extract a frame from the video file as the image.
         * When true, `action` is skipped entirely.
         */
        requestPoster?: boolean;

        /**
         * Crops the source to this ratio server-side before resizing, so the file that
         * arrives already has the shape the layout needs and no pixels are downloaded
         * only to be cut off by `object-fit`.
         *
         * @value `w:h`, e.g. `4:3`
         */
        aspectRatio?: string;

        /**
         * Which part of the source survives the `aspectRatio` crop.
         *
         * @default `center`
         */
        position?: 'center' | 'top' | 'bottom' | 'left' | 'right';

    }
}

export default MediaV2
